/**
 * Vue Aujourd'hui — CDC §3.4.
 *
 * Score Santé du jour avec décomposition cliquable, 3 cartes prioritaires
 * choisies par l'IA, bandeau d'actions rapides, alertes ouvertes.
 */
import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'

import { santeApi } from '../api/santeApi'
import {
  AXIS_LABELS,
  EmptyState,
  HEALTH_ACCENT,
  Loading,
  SectionTitle,
  cardSx,
  severityColor,
} from './shared'

function ScoreRing({ score }) {
  const angle = Math.round((score / 100) * 360)
  return (
    <Box
      sx={{
        width: 132,
        height: 132,
        borderRadius: '50%',
        background: `conic-gradient(${HEALTH_ACCENT} ${angle}deg, rgba(255,255,255,0.08) ${angle}deg)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          width: 104,
          height: 104,
          borderRadius: '50%',
          bgcolor: '#00001d',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: 32 }}>
          {score}
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>
          / 100
        </Typography>
      </Box>
    </Box>
  )
}

const CARD_COLORS = {
  alert: '#ff5d6c',
  action: HEALTH_ACCENT,
  reminder: '#ffb454',
  insight: '#5ab9ff',
}

export default function TodayView() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [measure, setMeasure] = useState(null) // {metric, label, unit}
  const [measureValue, setMeasureValue] = useState('')
  const [measureError, setMeasureError] = useState('')
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    const res = await santeApi.getDashboardToday()
    setData(res)
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const saveMeasure = async () => {
    if (!measure || measureValue === '') return
    // Tolère la virgule décimale française ("72,5") — le backend attend un point.
    const value = parseFloat(String(measureValue).replace(',', '.'))
    if (Number.isNaN(value)) {
      setMeasureError('Valeur invalide — saisissez un nombre.')
      return
    }
    setSaving(true)
    setMeasureError('')
    const created = await santeApi.events.create({
      metric: measure.metric,
      value,
      unit: measure.unit,
      recorded_at: new Date().toISOString(),
      source: 'manual',
    })
    setSaving(false)
    if (!created) {
      setMeasureError("L'enregistrement a échoué. Vérifiez la valeur puis réessayez.")
      return
    }
    setMeasure(null)
    setMeasureValue('')
    await santeApi.recomputeScore(null)
    load()
  }

  const acknowledge = async (id) => {
    await santeApi.acknowledgeAlert(id)
    load()
  }

  if (loading) return <Loading label="Construction de votre tableau de bord…" />
  if (!data) return <EmptyState>Tableau de bord indisponible pour le moment.</EmptyState>

  const breakdown = data.score?.breakdown || {}
  const axes = Object.keys(AXIS_LABELS).filter(
    (a) => breakdown[a] && breakdown[a].score != null,
  )

  return (
    <Box>
      {/* Score + décomposition */}
      <Box sx={{ ...cardSx, mb: 2 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item>
            <ScoreRing score={data.score?.score ?? 0} />
          </Grid>
          <Grid item xs>
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 18, mb: 0.5 }}>
              Score Santé du jour
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, mb: 1.5 }}>
              Composite et explicable — chaque axe contribue selon les données
              réellement enregistrées.
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {axes.length === 0 && (
                <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>
                  Ajoutez une mesure pour activer le calcul.
                </Typography>
              )}
              {axes.map((a) => (
                <Box
                  key={a}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.06)',
                    borderRadius: 2,
                    px: 1.5,
                    py: 0.75,
                  }}
                >
                  <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 11 }}>
                    {AXIS_LABELS[a]}
                  </Typography>
                  <Typography sx={{ color: HEALTH_ACCENT, fontWeight: 700 }}>
                    {breakdown[a].score}/100
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Grid>
        </Grid>
      </Box>

      {/* Bandeau d'actions rapides */}
      <Box sx={{ ...cardSx, mb: 2 }}>
        <SectionTitle>Actions rapides</SectionTitle>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          {(data.quick_actions || []).map((qa) => (
            <Button
              key={qa.metric}
              variant="outlined"
              onClick={() => {
                setMeasure(qa)
                setMeasureValue('')
                setMeasureError('')
              }}
              sx={{
                color: '#fff',
                borderColor: 'rgba(255,255,255,0.2)',
                textTransform: 'none',
              }}
            >
              {qa.label}
            </Button>
          ))}
        </Stack>
        {(data.latest_measures || []).length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
            {data.latest_measures.map((m) => (
              <Box
                key={m.metric}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.06)',
                  borderRadius: 2,
                  px: 1.5,
                  py: 0.75,
                }}
              >
                <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 11 }}>
                  {m.label}
                </Typography>
                <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>
                  {m.value} {m.unit}
                  <Typography
                    component="span"
                    sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, ml: 0.75 }}
                  >
                    {new Date(m.recorded_at).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'short',
                    })}
                  </Typography>
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
      </Box>

      {/* Cartes prioritaires */}
      <SectionTitle>Vos 3 priorités du jour</SectionTitle>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {(data.cards || []).map((card) => (
          <Grid item xs={12} md={4} key={card.id}>
            <Box
              sx={{
                ...cardSx,
                height: '100%',
                borderLeft: `4px solid ${CARD_COLORS[card.card_type] || HEALTH_ACCENT}`,
              }}
            >
              <Typography sx={{ color: '#fff', fontWeight: 700, mb: 0.5 }}>
                {card.title}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>
                {card.body}
              </Typography>
            </Box>
          </Grid>
        ))}
        {(data.cards || []).length === 0 && (
          <Grid item xs={12}>
            <EmptyState>Aucune priorité aujourd’hui — tout est en ordre.</EmptyState>
          </Grid>
        )}
      </Grid>

      {/* Alertes ouvertes */}
      {(data.alerts || []).length > 0 && (
        <>
          <SectionTitle>Alertes à passer en revue</SectionTitle>
          <Stack spacing={1.5}>
            {data.alerts.map((alert) => (
              <Box
                key={alert.id}
                sx={{
                  ...cardSx,
                  borderLeft: `4px solid ${severityColor(alert.severity)}`,
                }}
              >
                <Typography sx={{ color: '#fff', fontWeight: 600 }}>
                  {alert.message}
                </Typography>
                {alert.recommendation && (
                  <Typography
                    sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, mt: 0.5 }}
                  >
                    {alert.recommendation}
                  </Typography>
                )}
                <Button
                  size="small"
                  onClick={() => acknowledge(alert.id)}
                  sx={{ color: HEALTH_ACCENT, textTransform: 'none', mt: 0.5, px: 0 }}
                >
                  J’ai pris connaissance
                </Button>
              </Box>
            ))}
          </Stack>
        </>
      )}

      {/* Dialogue de saisie rapide */}
      <Dialog open={!!measure} onClose={() => setMeasure(null)}>
        <DialogTitle>{measure?.label}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            type="text"
            inputProps={{ inputMode: 'decimal' }}
            label={`Valeur (${measure?.unit || ''})`}
            value={measureValue}
            onChange={(e) => setMeasureValue(e.target.value)}
            fullWidth
            sx={{ mt: 1, minWidth: 280 }}
          />
          {measureError && (
            <Typography sx={{ color: '#ff5d6c', fontSize: 13, mt: 1 }}>
              {measureError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMeasure(null)}>Annuler</Button>
          <Button
            onClick={saveMeasure}
            variant="contained"
            disabled={saving}
            sx={{ bgcolor: HEALTH_ACCENT, color: '#00001d' }}
          >
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
